import React, { useState, useContext } from 'react';
import axios from 'axios';
import Dropdown from '../../utils/Dropdown';
import Loader from '../../utils/Loader';
import { CategoryContext } from '../../../Contexts/CategoryContext';
import { LoginContext } from '../../../Contexts/LoginContext';
import { showToast } from '../../utils/ShowToast';
import { FaInfoCircle } from 'react-icons/fa';

const TradeLeadModal = ({ onClose }) => {
    const { categories } = useContext(CategoryContext);
    const { user } = useContext(LoginContext);
    const [currentStep, setCurrentStep] = useState(1);
    const [isLoading, setIsLoading] = useState(false);
    const [formData, setFormData] = useState({
        product: '',
        category: '',
        subcategory: '',
        childcategory: '',
        event: '',
        sellingScope: 'local',
        state: '',
        city: '',
        location: '',
        kilometersAway: '',
        requirementDetails: '',
        budget: '',
        quantity: '',
        neededDate: '',
        neededTime: '',
        expiryDate: '',
        deliveryOption: 'Pick up',
    });

    const hasAddress = user?.buyerAddress?.country?.name;

    // Category scope detection
    const selectedCat = categories.find(c => c.name === formData.category);
    const categoryScope = selectedCat?.scope || 'local';
    const isInternational = formData.sellingScope === 'international';

    // Dynamic steps based on scope
    const getSteps = () => {
        const steps = [
            { title: 'Product Details', fields: ['product', 'category', 'subcategory', 'childcategory', 'event'] },
        ];
        // Only show Preferred Area step for local scope
        if (!isInternational) {
            steps.push({ title: 'Preferred Area', fields: ['state', 'city', 'location', 'kilometersAway'] });
        }
        steps.push(
            { title: 'Requirement Details', fields: ['requirementDetails', 'budget', 'quantity'] },
            { title: 'Date & Time', fields: ['neededDate', 'neededTime', 'expiryDate'] },
            { title: 'Delivery Option', fields: ['deliveryOption'] },
        );
        return steps;
    };

    const steps = getSteps();

    // Field configuration
    const fieldConfig = {
        product: { type: 'text', label: 'Product', required: true },
        category: { type: 'dropdown', label: 'Category', options: categories.map(cat => ({ value: cat.name, label: cat.name })), required: true },
        subcategory: { type: 'dropdown', label: 'Sub-Category', options: [], required: true },
        childcategory: { type: 'dropdown', label: 'Child Category', options: [], required: false },
        event: { type: 'text', label: 'Event', required: true },
        state: { type: 'text', label: 'State', required: true },
        city: { type: 'text', label: 'City', required: true },
        location: { type: 'text', label: 'Location/Area', required: false },
        kilometersAway: {
            type: 'dropdown',
            label: 'Preferred Range',
            options: [
                { value: '<10', label: '< 10 km' },
                { value: '10-25', label: '10-25 km' },
                { value: '25-50', label: '25-50 km' },
                { value: '50-100', label: '50-100 km' },
                { value: '100-500', label: '100-500 km' },
                { value: '500+', label: '500+ km' },
            ],
            required: true,
        },
        requirementDetails: { type: 'textarea', label: 'Requirements', required: true },
        budget: { type: 'number', label: 'Targeted Budget ($)', required: true },
        quantity: { type: 'number', label: 'Quantity', required: true },
        neededDate: { type: 'date', label: 'Date Needed', required: true },
        neededTime: { type: 'time', label: 'Time Needed', required: true },
        expiryDate: { type: 'date', label: 'Expiry Date', required: true },
        deliveryOption: {
            type: 'radio',
            label: 'Delivery Option',
            options: ['Pick up', 'Home Delivery'],
            required: true,
        },
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value,
            ...(name === 'category' && { subcategory: '', childcategory: '' }),
            ...(name === 'subcategory' && { childcategory: '' }),
        }));
    };

    const handleDropdownChange = (name, selected) => {
        if (name === 'category') {
            const cat = categories.find(c => c.name === selected?.value);
            setFormData(prev => ({
                ...prev,
                category: selected?.value || '',
                subcategory: '',
                childcategory: '',
                sellingScope: cat?.scope === 'local' ? 'local' : prev.sellingScope,
            }));
        } else {
            setFormData(prev => ({
                ...prev,
                [name]: selected?.value || '',
                ...(name === 'subcategory' && { childcategory: '' }),
            }));
        }
    };

    const getSubcategoryOptions = () => {
        return formData.category
            ? categories.find(cat => cat.name === formData.category)?.subcategories.map(sub => ({ value: sub.name, label: sub.name })) || []
            : [];
    };

    const getChildcategoryOptions = () => {
        if (!formData.category || !formData.subcategory) return [];
        const cat = categories.find(c => c.name === formData.category);
        const sub = cat?.subcategories?.find(s => s.name === formData.subcategory);
        return sub?.children?.map(child => ({ value: child.name, label: child.name })) || [];
    };

    const isStepValid = () => {
        const currentFields = steps[currentStep - 1].fields;
        return currentFields.every(field => {
            const config = fieldConfig[field];
            if (!config?.required) return true;
            return formData[field] && formData[field].toString().trim() !== '';
        });
    };

    const validateForm = () => {
        if (!hasAddress) {
            showToast('Please set your address in Settings before posting a requirement', 'warning');
            return false;
        }
        const today = new Date();
        const neededDate = new Date(formData.neededDate);
        const expiryDate = new Date(formData.expiryDate);
        const todayStart = new Date(today.setHours(0, 0, 0, 0));

        if (Number(formData.budget) <= 0) {
            showToast('Budget must be greater than 0', 'error');
            return false;
        }
        if (Number(formData.quantity) < 1) {
            showToast('Quantity must be at least 1', 'error');
            return false;
        }
        if (neededDate < todayStart) {
            showToast('Needed date must be today or later', 'error');
            return false;
        }
        if (neededDate.toDateString() === todayStart.toDateString()) {
            const [hours, minutes] = formData.neededTime.split(':').map(Number);
            const neededTime = new Date();
            neededTime.setHours(hours, minutes, 0, 0);
            if (neededTime <= today) {
                showToast('Needed time must be later than current time', 'error');
                return false;
            }
        }
        const tomorrow = new Date(todayStart);
        tomorrow.setDate(tomorrow.getDate() + 1);
        if (expiryDate < tomorrow) {
            showToast('Expiry date must be at least one day later than today', 'error');
            return false;
        }
        return true;
    };

    const handleNext = () => {
        if (isStepValid()) {
            setCurrentStep(currentStep + 1);
        } else {
            showToast('Please fill all required fields', 'warning');
        }
    };

    const handleBack = () => {
        setCurrentStep(currentStep - 1);
    };

    const handleSubmit = async () => {
        if (!isStepValid()) {
            showToast('Please fill all required fields', 'warning');
            return;
        }
        if (!validateForm()) return;

        setIsLoading(true);
        try {
            const token = localStorage.getItem('token');
            const config = { headers: { Authorization: `Bearer ${token}` } };
            const payload = { ...formData };
            if (payload.sellingScope === 'international') {
                delete payload.state;
                delete payload.city;
                delete payload.location;
                delete payload.kilometersAway;
            }
            await axios.post(`${process.env.REACT_APP_Backend_Host_Name}/api/v1/tradeleads`, payload, config);
            showToast('Tradelead posted successfully', 'success');
            onClose();
        } catch (error) {
            showToast(error.response?.data?.message || 'Error submitting tradelead', 'error');
            console.error(error);
        } finally {
            setIsLoading(false);
        }
    };

    const renderFields = () => {
        const currentFields = steps[currentStep - 1].fields;
        return (
            <>
                {currentFields.map(field => {
                    const config = fieldConfig[field];
                    return (
                        <div key={field} className="tradeLeadField" style={{ marginBottom: '16px' }}>
                            <label htmlFor={field}>{config.label}</label>
                            {config.type === 'dropdown' ? (
                                <Dropdown
                                    id={field}
                                    options={field === 'subcategory' ? getSubcategoryOptions() : field === 'childcategory' ? getChildcategoryOptions() : config.options}
                                    value={formData[field] ? { value: formData[field], label: formData[field] } : null}
                                    onChange={(selected) => handleDropdownChange(field, selected)}
                                    placeholder={`Select ${config.label}`}
                                    isClearable
                                    isDisabled={field === 'subcategory' && !formData.category}
                                />
                            ) : config.type === 'textarea' ? (
                                <textarea
                                    id={field}
                                    className="inputField"
                                    name={field}
                                    value={formData[field]}
                                    onChange={handleChange}
                                    placeholder={`Enter ${config.label}`}
                                    required={config.required}
                                    style={{ width: '100%', minHeight: '80px' }}
                                />
                            ) : config.type === 'radio' ? (
                                <div className="deliveryOptions" style={{ display: 'flex', gap: '20px' }}>
                                    {config.options.map(option => (
                                        <label key={option}>
                                            <input
                                                type="radio"
                                                name={field}
                                                value={option}
                                                checked={formData[field] === option}
                                                onChange={handleChange}
                                            />
                                            {option}
                                        </label>
                                    ))}
                                </div>
                            ) : (
                                <input
                                    id={field}
                                    type={config.type}
                                    className="inputField"
                                    name={field}
                                    value={formData[field]}
                                    onChange={handleChange}
                                    placeholder={`Enter ${config.label}`}
                                    required={config.required}
                                    min={config.type === 'date' && field === 'neededDate' ? new Date().toISOString().split('T')[0] :
                                        config.type === 'date' && field === 'expiryDate' ? new Date(Date.now() + 86400000).toISOString().split('T')[0] : undefined}
                                    style={{ width: '100%' }}
                                />
                            )}
                        </div>
                    );
                })}

                {/* Scope info/selection — shown in step 1 after category is selected */}
                {currentStep === 1 && selectedCat && (
                    <div style={{ marginBottom: '16px' }}>
                        {categoryScope === 'local' && (
                            <div className="cpInfoBanner">
                                <FaInfoCircle />
                                <p>Products/services in this category are only available locally.</p>
                            </div>
                        )}
                        {categoryScope === 'international' && (
                            <div className="tradeLeadField">
                                <label>Buying Scope</label>
                                <Dropdown
                                    options={[
                                        { value: 'local', label: 'Buy Locally' },
                                        { value: 'international', label: 'Buy Internationally' },
                                    ]}
                                    value={{
                                        value: formData.sellingScope,
                                        label: formData.sellingScope === 'local' ? 'Buy Locally' : 'Buy Internationally'
                                    }}
                                    onChange={(selected) => setFormData(prev => ({ ...prev, sellingScope: selected.value }))}
                                    placeholder="Select buying scope"
                                />
                            </div>
                        )}
                    </div>
                )}

                {/* Preferred area helper text for local scope */}
                {steps[currentStep - 1].title === 'Preferred Area' && (
                    <p style={{ fontSize: '12px', color: '#6b7280', marginTop: '-8px' }}>
                        Your country ({user?.buyerAddress?.country?.name}) is automatically used from your profile.
                    </p>
                )}
            </>
        );
    };

    const renderStepProgress = () => (
        <div className="stepContainer" style={{ display: 'flex', justifyContent: 'center', marginBottom: '20px' }}>
            {steps.map((step, index) => (
                <div key={index} className={`stepBlock ${currentStep >= index + 1 ? 'active' : ''}`} style={{ display: 'flex', alignItems: 'center' }}>
                    <div className={`step ${currentStep >= index + 1 ? 'active' : ''}`} style={{ width: '30px', height: '30px', borderRadius: '50%', background: currentStep >= index + 1 ? '#ff6b81' : '#ccc', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        {index + 1}
                    </div>
                    {index < steps.length - 1 && (
                        <div className={`stepLine ${currentStep > index + 1 ? 'filled' : ''}`} style={{ width: '50px', height: '2px', background: currentStep > index + 1 ? '#ff6b81' : '#ccc' }}></div>
                    )}
                </div>
            ))}
        </div>
    );

    // No address — show message
    if (!hasAddress) {
        return (
            <div className="modalOverlay">
                <div className="modalContent" style={{ position: 'relative', textAlign: 'center', padding: '40px 30px' }}>
                    <button
                        onClick={onClose}
                        style={{ position: 'absolute', top: '10px', right: '10px', background: 'none', border: 'none', fontSize: '30px', cursor: 'pointer' }}
                    >
                        &times;
                    </button>
                    <h2>Address Required</h2>
                    <p style={{ color: '#6b7280', marginBottom: '20px' }}>
                        Please set your address in Settings before posting a requirement. Sellers need your location to provide accurate quotes.
                    </p>
                    <button
                        className="primaryBtn"
                        onClick={() => { onClose(); window.location.href = '/settings'; }}
                    >
                        Go to Settings
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="modalOverlay">
            {isLoading && <Loader type="checkmate" />}
            <div className="modalContent" style={{ position: 'relative', maxWidth: "600px" }}>
                <button
                    onClick={onClose}
                    style={{ position: 'absolute', top: '10px', right: '10px', background: 'none', border: 'none', fontSize: '30px', cursor: 'pointer' }}
                >
                    &times;
                </button>
                <h2>{"Post a Request"}</h2>
                {renderStepProgress()}
                <form onSubmit={(e) => e.preventDefault()}>
                    {renderFields()}
                    <div className="navButtons">
                        {currentStep > 1 && (
                            <button type="button" onClick={handleBack} className="primaryBtn3">
                                Back
                            </button>
                        )}
                        {currentStep < steps.length ? (
                            <button type="button" onClick={handleNext} className="primaryBtn" >
                                Next
                            </button>
                        ) : (
                            <button type="button" onClick={handleSubmit} className="primaryBtn">
                                Submit
                            </button>
                        )}
                    </div>
                </form>
            </div>
        </div>
    );
};

export default TradeLeadModal;